/**
 * Draft Service - 임시저장 기능을 위한 Firestore CRUD 함수들
 *
 * Firestore 구조:
 * PPT_FILE/{doc_id}/drafts/
 *   - color_draft
 *   - font_draft
 *   - typo_draft
 */

import { doc, getDoc, setDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Draft 유효기간 (30일)
const DRAFT_EXPIRY_DAYS = 30;

/**
 * 만료 시간 계산 (현재 시간 + 30일)
 */
const getExpiryTimestamp = () => {
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + DRAFT_EXPIRY_DAYS);
  return Timestamp.fromDate(expiryDate);
};

/**
 * Draft가 만료되었는지 확인
 */
const isDraftExpired = (draft) => {
  if (!draft?.expires_at) return false;
  const expiryDate = draft.expires_at.toDate();
  return new Date() > expiryDate;
};

// ==================== 칼라 Draft ====================

/**
 * 칼라 변경사항 draft 저장
 * @param {string} docId - PPT 문서 ID
 * @param {string} userId - 사용자 ID
 * @param {Object} changes - { slideIndex: { violationIndex: newColor } }
 */
export const saveColorDraft = async (docId, userId, changes) => {
  if (!docId || !userId) return false;

  // 빈 객체면 저장하지 않음
  if (!changes || Object.keys(changes).length === 0) {
    return false;
  }

  try {
    const draftRef = doc(db, 'PPT_FILE', docId, 'drafts', 'color_draft');
    await setDoc(draftRef, {
      user_id: userId,
      changes: changes,
      updated_at: Timestamp.now(),
      expires_at: getExpiryTimestamp()
    });
    console.log('[draftService] 칼라 draft 저장 완료');
    return true;
  } catch (error) {
    console.error('[draftService] 칼라 draft 저장 오류:', error);
    return false;
  }
};

/**
 * 칼라 변경사항 draft 로드
 * @param {string} docId - PPT 문서 ID
 * @param {string} userId - 사용자 ID
 * @returns {Object|null} - changes 객체 또는 null
 */
export const loadColorDraft = async (docId, userId) => {
  if (!docId || !userId) return null;

  try {
    const draftRef = doc(db, 'PPT_FILE', docId, 'drafts', 'color_draft');
    const draftSnap = await getDoc(draftRef);

    if (!draftSnap.exists()) return null;

    const draft = draftSnap.data();

    // 다른 사용자의 draft면 무시
    if (draft.user_id !== userId) return null;

    // 만료된 draft면 삭제하고 null 반환
    if (isDraftExpired(draft)) {
      await deleteColorDraft(docId);
      return null;
    }

    return draft.changes || null;
  } catch (error) {
    console.error('[draftService] 칼라 draft 로드 오류:', error);
    return null;
  }
};

/**
 * 칼라 변경사항 draft 삭제
 * @param {string} docId - PPT 문서 ID
 */
export const deleteColorDraft = async (docId) => {
  if (!docId) return false;

  try {
    const draftRef = doc(db, 'PPT_FILE', docId, 'drafts', 'color_draft');
    await deleteDoc(draftRef);
    console.log('[draftService] 칼라 draft 삭제 완료');
    return true;
  } catch (error) {
    console.error('[draftService] 칼라 draft 삭제 오류:', error);
    return false;
  }
};

// ==================== 폰트 Draft ====================

/**
 * 폰트 변경사항 draft 저장
 * @param {string} docId - PPT 문서 ID
 * @param {string} userId - 사용자 ID
 * @param {Object} changes - { slideIndex: { violationIndex: newFont } }
 */
export const saveFontDraft = async (docId, userId, changes) => {
  if (!docId || !userId) return false;

  // 빈 객체면 저장하지 않음
  if (!changes || Object.keys(changes).length === 0) {
    return false;
  }

  try {
    const draftRef = doc(db, 'PPT_FILE', docId, 'drafts', 'font_draft');
    await setDoc(draftRef, {
      user_id: userId,
      changes: changes,
      updated_at: Timestamp.now(),
      expires_at: getExpiryTimestamp()
    });
    console.log('[draftService] 폰트 draft 저장 완료');
    return true;
  } catch (error) {
    console.error('[draftService] 폰트 draft 저장 오류:', error);
    return false;
  }
};

/**
 * 폰트 변경사항 draft 로드
 * @param {string} docId - PPT 문서 ID
 * @param {string} userId - 사용자 ID
 * @returns {Object|null} - changes 객체 또는 null
 */
export const loadFontDraft = async (docId, userId) => {
  if (!docId || !userId) return null;

  try {
    const draftRef = doc(db, 'PPT_FILE', docId, 'drafts', 'font_draft');
    const draftSnap = await getDoc(draftRef);

    if (!draftSnap.exists()) return null;

    const draft = draftSnap.data();

    // 다른 사용자의 draft면 무시
    if (draft.user_id !== userId) return null;

    // 만료된 draft면 삭제하고 null 반환
    if (isDraftExpired(draft)) {
      await deleteFontDraft(docId);
      return null;
    }

    return draft.changes || null;
  } catch (error) {
    console.error('[draftService] 폰트 draft 로드 오류:', error);
    return null;
  }
};

/**
 * 폰트 변경사항 draft 삭제
 * @param {string} docId - PPT 문서 ID
 */
export const deleteFontDraft = async (docId) => {
  if (!docId) return false;

  try {
    const draftRef = doc(db, 'PPT_FILE', docId, 'drafts', 'font_draft');
    await deleteDoc(draftRef);
    console.log('[draftService] 폰트 draft 삭제 완료');
    return true;
  } catch (error) {
    console.error('[draftService] 폰트 draft 삭제 오류:', error);
    return false;
  }
};

// ==================== 오타 Draft ====================

/**
 * 오타 변경사항 draft 저장
 * @param {string} docId - PPT 문서 ID
 * @param {string} userId - 사용자 ID
 * @param {Object} selectedTypos - { slideIndex: [typoIndex, ...] }
 * @param {Object} editedSuggestions - { "slideIndex_typoIndex": "수정된 제안" }
 */
export const saveTypoDraft = async (docId, userId, selectedTypos, editedSuggestions) => {
  if (!docId || !userId) return false;

  // 둘 다 빈 객체면 저장하지 않음
  const hasSelectedTypos = selectedTypos && Object.keys(selectedTypos).length > 0;
  const hasEditedSuggestions = editedSuggestions && Object.keys(editedSuggestions).length > 0;

  if (!hasSelectedTypos && !hasEditedSuggestions) {
    return false;
  }

  try {
    const draftRef = doc(db, 'PPT_FILE', docId, 'drafts', 'typo_draft');
    await setDoc(draftRef, {
      user_id: userId,
      selected_typos: selectedTypos || {},
      edited_suggestions: editedSuggestions || {},
      updated_at: Timestamp.now(),
      expires_at: getExpiryTimestamp()
    });
    console.log('[draftService] 오타 draft 저장 완료');
    return true;
  } catch (error) {
    console.error('[draftService] 오타 draft 저장 오류:', error);
    return false;
  }
};

/**
 * 오타 변경사항 draft 로드
 * @param {string} docId - PPT 문서 ID
 * @param {string} userId - 사용자 ID
 * @returns {Object|null} - { selected_typos, edited_suggestions } 또는 null
 */
export const loadTypoDraft = async (docId, userId) => {
  if (!docId || !userId) return null;

  try {
    const draftRef = doc(db, 'PPT_FILE', docId, 'drafts', 'typo_draft');
    const draftSnap = await getDoc(draftRef);

    if (!draftSnap.exists()) return null;

    const draft = draftSnap.data();

    // 다른 사용자의 draft면 무시
    if (draft.user_id !== userId) return null;

    // 만료된 draft면 삭제하고 null 반환
    if (isDraftExpired(draft)) {
      await deleteTypoDraft(docId);
      return null;
    }

    return {
      selected_typos: draft.selected_typos || {},
      edited_suggestions: draft.edited_suggestions || {}
    };
  } catch (error) {
    console.error('[draftService] 오타 draft 로드 오류:', error);
    return null;
  }
};

/**
 * 오타 변경사항 draft 삭제
 * @param {string} docId - PPT 문서 ID
 */
export const deleteTypoDraft = async (docId) => {
  if (!docId) return false;

  try {
    const draftRef = doc(db, 'PPT_FILE', docId, 'drafts', 'typo_draft');
    await deleteDoc(draftRef);
    console.log('[draftService] 오타 draft 삭제 완료');
    return true;
  } catch (error) {
    console.error('[draftService] 오타 draft 삭제 오류:', error);
    return false;
  }
};

// ==================== 유틸리티 ====================

/**
 * 모든 draft 삭제 (새로운 분석 시작 시 호출)
 * @param {string} docId - PPT 문서 ID
 * @param {string} type - 'color' | 'font' | 'typo' | 'all'
 */
export const deleteDraftByType = async (docId, type) => {
  if (!docId) return false;

  try {
    switch (type) {
      case 'color':
        await deleteColorDraft(docId);
        break;
      case 'font':
        await deleteFontDraft(docId);
        break;
      case 'typo':
        await deleteTypoDraft(docId);
        break;
      case 'all':
        await Promise.all([
          deleteColorDraft(docId),
          deleteFontDraft(docId),
          deleteTypoDraft(docId)
        ]);
        break;
      default:
        console.warn('[draftService] 알 수 없는 draft 타입:', type);
        return false;
    }
    return true;
  } catch (error) {
    console.error('[draftService] draft 삭제 오류:', error);
    return false;
  }
};
